import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Paper,
  Alert
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningIcon from '@mui/icons-material/Warning';
import SkillsList from '../components/skills/SkillsList';
import SkillForm from '../components/skills/SkillForm';
import { useSkills } from '../contexts/SkillsContext';
import { useTeam } from '../context/TeamContext';
import { useAuth } from '../context/AuthContext';
import PageTitle from '../components/layout/PageTitle';

const SkillsPage = () => {
  const { skills, loading, error, deleteSkill } = useSkills();
  const { selectedTeam } = useTeam();
  const { currentUser } = useAuth();
  const [openForm, setOpenForm] = useState(false);
  const [selectedSkill, setSelectedSkill] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    skill: null
  });

  // Vérifier les permissions pour l'équipe sélectionnée
  const hasPermission = (action) => {
    console.log('🔍 Vérification permissions compétences:', { currentUser: currentUser?.email, selectedTeam: selectedTeam?.name, action });
    if (!currentUser || !selectedTeam) return false;
    
    // SuperAdmin peut tout faire
    if (currentUser.role === 'SUPER_ADMIN') return true;
    
    // Admin ou adjoint de l'équipe peut tout faire
    if (selectedTeam.adminUserId === currentUser.id || 
        selectedTeam.assistantAdminUserId === currentUser.id) {
      return true;
    }
    
    // Vérifier les permissions de délégation
    const permissions = selectedTeam.settings?.permissions?.skills || 
                       { view: true, add: false, edit: false, delete: false };
    
    return permissions[action] || false;
  };

  const handleOpenForm = () => {
    setSelectedSkill(null);
    setOpenForm(true);
  };

  const handleCloseForm = () => {
    setOpenForm(false);
    setSelectedSkill(null);
  };

  const handleEdit = (skill) => {
    setSelectedSkill(skill);
    setOpenForm(true);
  };

  const handleDelete = (skill) => {
    setDeleteDialog({
      open: true,
      skill: skill
    });
  };

  const handleDeleteConfirm = async () => {
    if (deleteDialog.skill) {
      try {
        await deleteSkill(deleteDialog.skill.id);
        setDeleteDialog({ open: false, skill: null });
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
      }
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, skill: null });
  };

  const handleSave = () => {
    // Plus besoin de recharger manuellement car le contexte se met à jour automatiquement
    handleCloseForm();
  };

  if (loading) {
    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <PageTitle title="Gestion des Compétences" />
        <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', mt: 4 }}>
          Chargement des compétences...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <PageTitle
        title="Gestion des Compétences"
        actions={
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenForm}
            disabled={!hasPermission('add')}
            title={!hasPermission('add') ? 'Vous n\'avez pas la permission d\'ajouter des compétences' : ''}
          >
            Ajouter une compétence
          </Button>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {skills.length === 0 ? (
        <Paper 
          sx={{ 
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2
          }}
        >
          <Typography variant="body1" color="text.secondary">
            Aucune compétence n'a été ajoutée.
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenForm}
            disabled={!hasPermission('add')}
            title={!hasPermission('add') ? 'Vous n\'avez pas la permission d\'ajouter des compétences' : ''}
          >
            Ajouter votre première compétence
          </Button>
        </Paper>
      ) : (
        <SkillsList
          skills={skills}
          onEdit={handleEdit}
          onDelete={handleDelete}
          hasPermission={hasPermission}
        />
      )}

      {/* Dialogue de formulaire */}
      <Dialog
        open={openForm}
        onClose={handleCloseForm}
        maxWidth="sm"
        fullWidth
      >
        <SkillForm
          skill={selectedSkill}
          onClose={handleCloseForm}
          onSuccess={handleSave}
        />
      </Dialog>

      {/* Dialogue de confirmation de suppression */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          Confirmer la suppression
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            {deleteDialog.skill && (
              <>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '8px',
                    backgroundColor: deleteDialog.skill.color,
                    flexShrink: 0,
                    border: '2px solid',
                    borderColor: `${deleteDialog.skill.color}40`
                  }}
                />
                <Box>
                  <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 500 }}>
                    {deleteDialog.skill.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Taux de couverture : {deleteDialog.skill.coverageIndex}%
                  </Typography>
                </Box>
              </>
            )}
          </Box>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Cette action est irréversible. La compétence sera définitivement supprimée.
          </Alert>
          <Typography variant="body1">
            Êtes-vous sûr de vouloir supprimer cette compétence ?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleDeleteCancel}>
            Annuler
          </Button>
          <Button 
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
          >
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SkillsPage;
